import {Injectable, Output, Input, ElementRef, inject} from '@angular/core';
import * as fabric from 'fabric';
import {ShapeDraw} from '@core/enums/shape_draw';
import {configuration} from '@core/data/config';
import {FormControl} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';

@Injectable({
  providedIn: 'root'
})
export class ShapeDrawService {

  private shapeDraw = ShapeDraw;
  private fileManagerService: FilemanagerApiService = inject(FilemanagerApiService);
  canvasElement!: ElementRef;
  canvas!: fabric.Canvas;
  imageBase64: string = '';
  selectedShape: string = this.shapeDraw.POINTER;
  selectedColor = configuration.default_color_draw;
  sizeFontValue = configuration.default_font_size_draw;
  sizeValue = configuration.default_size_stroke_draw;
  croppingRect: fabric.Rect | null = null;
  public selectedShapeControl = new FormControl(this.shapeDraw.POINTER);

  private deleteIcon = "assets/images/icon/delete.svg";
  private cloneIcon = "assets/images/icon/clone.svg";
  isMultipleKeyDown: boolean = false;

  constructor() {
  }

  initCanvas(canvasElement: ElementRef): void {
    this.canvasElement = canvasElement;
    const isMacLike = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const selectionKey = isMacLike ? "metaKey" : "ctrlKey";
    
    this.canvas = new fabric.Canvas(this.canvasElement.nativeElement, {
      backgroundColor: 'transparent',
      selection: true,
      selectionKey: selectionKey
    });
    
    this.addEventDrawPencil();
    this.addEventDraw();
    // this.addEventSelect();
  }

  getCanvasElement(): fabric.Canvas {
    return this.canvas;
  }

  private addEventDrawPencil() {
    this.canvas.on('path:created', (event) => {
      const path = event.path;
      if (path) {
        path.set({
          originX: 'left',
          originY: 'top'
        });
        path.setCoords();
        this.addDeleteControl(path);
        this.canvas.isDrawingMode = false;
        this.canvas.renderAll();
      }
    });
  }

  private addEventDraw(): void {
    this.canvas.on('mouse:down', (event: any) => {
      if (!this.canvas.getActiveObject()) {
        this.addShape(event.pointer.x, event.pointer.y);
      } else {
        const selectedObject = this.canvas.getActiveObject();
        this.selectShapeForModification(selectedObject);
      }
    });
  }

  setIsMultipleKeyDown(isMultipleKeyDown: boolean): void {
    this.isMultipleKeyDown = isMultipleKeyDown;
  }

  multiSelection: any[] = [];
  private addEventSelect(): void {
    this.canvas.on('selection:created', (e : any) => {
      const selected = e.selected[0];
      if(this.isMultipleKeyDown) {
        if(this.multiSelection.includes(selected)) {
          this.multiSelection.splice(this.multiSelection.indexOf(selected), 1);
        } else {
          this.multiSelection.push(selected);
        }
        var groupSelection = new fabric.ActiveSelection(this.multiSelection)
        this.canvas.setActiveObject(groupSelection);
      }
    });

    this.canvas.on('selection:updated', (e : any) => {
      console.log(this.canvas.getActiveObject());
      const selected = e.selected[0];
      if(this.isMultipleKeyDown) {
        if(this.multiSelection.includes(selected)) {
          this.multiSelection.splice(this.multiSelection.indexOf(selected), 1);
        } else {
          this.multiSelection.push(selected);
        }
        var groupSelection = new fabric.ActiveSelection(this.multiSelection)
        this.canvas.setActiveObject(groupSelection);
      }
    });

    this.canvas.on('selection:cleared', () => {
      this.multiSelection = [];
    });
  }

  setImageBackground(image64BG: string | null = null): void {
    if (image64BG) {
      this.imageBase64 = image64BG;
    }
    if (this.imageBase64) {
      fabric.FabricImage.fromURL(this.imageBase64).then((img) => {
        const scale = Math.min(
          this.canvas.width / img.width,
          this.canvas.height / img.height
        );
        const left = (this.canvas.width - img.width * scale) / 2;
        const top = (this.canvas.height - img.height * scale) / 2;
        img.set({
          left: left,
          top: top,
          //scaleX: this.canvas.width / img.width,
          scaleX: scale,
          //scaleY: this.canvas.height / img.height,
          scaleY: scale,
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          background: false
        });
        this.canvas.add(img);
        this.canvas.renderAll();
      });
    }
  }

  private normalizeObject(obj: fabric.Object): void {
    obj.set({
      originX: 'left',
      originY: 'top'
    });
    obj.setCoords();
  }

  setShape(value: string | null = null) {
    if (value) {
      this.selectedShapeControl.setValue(value as ShapeDraw, {emitEvent: false});
      this.selectedShape = value;
    }
  }

  addShape(x: number, y: number) {
    this.disableFreeDraw();
    switch (this.selectedShapeControl.value) {
      case this.shapeDraw.POINTER:
        this.clearActiveDrawing();
        break;
      case this.shapeDraw.RECTANGLE:
        this.addRectangle(x, y);
        break;
      case this.shapeDraw.CIRCLE:
        this.addCircle(x, y);
        break;
      case this.shapeDraw.LINE:
        this.addLine(x, y);
        break;
      case this.shapeDraw.ARROW:
        this.addArrow(x, y);
        break;
      case this.shapeDraw.SCRIBBLE:
        this.addFreeDraw();
        break;
      case this.shapeDraw.TEXT:
        this.addText(x, y);
        break;
      case this.shapeDraw.START_CROP:
        this.startCrop();
        break;
      case this.shapeDraw.STOP_CROP:
        // this.stopCrop();
        break;
      default:
        break;
    }
  }

  startCrop() {
    this.croppingRect = new fabric.Rect({
      left: 50,
      top: 50,
      width: 200,
      height: 200,
      fill: 'rgba(0,0,0,0.3)',
      hasBorders: true,
      hasControls: true,
      selectable: true,
    });

    this.canvas.add(this.croppingRect);
    this.canvas.setActiveObject(this.croppingRect);
    this.canvas.renderAll();

    // this.setShape(ShapeDraw.POINTER);
  }

  stopCrop() {
    if (!this.croppingRect) {
      return;
    }
    this.canvas.remove(this.croppingRect);
    const croppedDataURL = this.canvas.toDataURL({
      left: this.croppingRect.left,
      top: this.croppingRect.top,
      width: this.croppingRect.width * this.croppingRect.scaleX,
      height: this.croppingRect.height * this.croppingRect.scaleY,
      quality: 1,
      multiplier: 4,
    });
    this.canvas.clear();
    this.croppingRect = null;
    this.setShape(ShapeDraw.POINTER);
    return croppedDataURL;
  }

  addText(x: number, y: number): void {
    const shape = new fabric.Textbox('Write your text here...', {
      left: x,
      top: y,
      width: 200,
      minWidth: 200,
      height: 50,
      minHeight: 50,
      fill: this.selectedColor,
      fontSize: this.sizeFontValue ?? 40,
      editable: true,
      selectable: true,
      textAlign: 'left',
      padding: 10,
    });
    this.addDeleteControl(shape);
    this.canvas.add(shape);
    this.canvas.setActiveObject(shape);
    shape.enterEditing();
    shape.selectAll();
    this.setShape(ShapeDraw.POINTER);
  }

  addArrow(x: number, y: number): void {
    const lineLength = 100;
    const arrowSize = 20;
    const line = new fabric.Line([x, y, x + lineLength, y], {
      fill: 'transparent',
      stroke: this.selectedColor,
      strokeWidth: this.sizeValue ?? 2,
      selectable: true
    });
    const angle = Math.atan2(y - line.y1, x - line.x2);
    const arrowHead = new fabric.Triangle({
      left: x - 10,
      top: y - 1,
      width: arrowSize,
      height: arrowSize,
      fill: this.selectedColor,
      originX: 'center',
      originY: 'center',
      angle: (angle * 180) / Math.PI + 90
    });
    const arrowGroup = new fabric.Group([line, arrowHead], {
      left: x,
      top: y,
      angle: 0
    });
    this.addDeleteControl(arrowGroup);
    this.canvas.add(arrowGroup);
    this.setShape(ShapeDraw.POINTER);
  }

  addCircle(x: number, y: number): void {
    const shape = new fabric.Circle({
      left: x,
      top: y,
      radius: 50,
      fill: 'transparent',
      stroke: this.selectedColor,
      strokeWidth: this.sizeValue ?? 2,
    });
    this.addDeleteControl(shape);
    this.normalizeObject(shape);
    this.canvas.add(shape);
    this.setShape(ShapeDraw.POINTER);
  }

  addLine(x: number, y: number): void {
    const shape = new fabric.Line([x, y, x + 100, y + 100], {
      fill: 'transparent',
      stroke: this.selectedColor,
      strokeWidth: this.sizeValue ?? 2,
      selectable: true
    });
    this.addDeleteControl(shape);
    this.canvas.add(shape);
    this.setShape(ShapeDraw.POINTER);
  }

  addRectangle(x: number, y: number): void {
    const shape = new fabric.Rect({
      left: x,
      top: y,
      fill: 'transparent',
      stroke: this.selectedColor,
      strokeWidth: this.sizeValue ?? 2,
      width: 50,
      height: 50,
      angle: 0
    });
    this.addDeleteControl(shape);
    this.normalizeObject(shape);
    this.canvas.add(shape);
    this.setShape(ShapeDraw.POINTER);
  }

  addFreeDraw(): void {
    this.canvas.isDrawingMode = true;
    this.canvas.freeDrawingBrush = new fabric.PencilBrush(this.canvas);
    this.canvas.freeDrawingBrush.width = this.sizeValue ?? 2;
    this.canvas.freeDrawingBrush.color = this.selectedColor;
  }

  disableFreeDraw(): void {
    this.canvas.isDrawingMode = false;
  }

  clearCanvas(): void {
    const activeObjects = this.canvas.getActiveObjects();
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => {
        this.canvas.discardActiveObject();
        this.canvas.remove(obj);
        this.canvas.renderAll();
        this.canvas.requestRenderAll();
      });
    } else {
      this.canvas.clear();
      this.canvas.backgroundColor = '#fff';
      this.canvas.renderAll();
      this.canvas.requestRenderAll();
      this.setImageBackground();
    }
    this.clearOnlySelectedDrawings();
  }

  clearOnlySelectedDrawings(): void {
    const activePaths = this.canvas.getActiveObjects().filter(obj => obj.type === 'path');
    if (activePaths.length > 0) {
      activePaths.forEach(path => this.canvas.remove(path));
      this.canvas.discardActiveObject();
      this.canvas.requestRenderAll();
    }
  }

  clearActiveDrawing(): void {
    this.canvas.isDrawingMode = false;
    this.canvas.off('mouse:move');
    this.canvas.off('mouse:up');
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  selectShapeForModification(selectedObject: any): void {
    if (selectedObject) {
      this.canvas.setActiveObject(selectedObject);
    }
  }

  addDeleteControl(shape: fabric.Object): void {
    const deleteControl = new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetY: 16,
      cursorStyle: 'pointer',
      mouseUpHandler: this.deleteObject.bind(this),
      render: this.renderDeleteIcon.bind(this),
    });
    shape.controls['deleteControl'] = deleteControl;
  }

  deleteObject(_eventData: any, transform: any): void {
    const targetObject = transform?.target || this.canvas.getActiveObject();  // Prendi l'oggetto selezionato
    if (targetObject) {
      if (targetObject.type === 'path') {
        this.clearOnlySelectedDrawings();
      } else {
        this.canvas.remove(targetObject);
      }
      this.canvas.requestRenderAll();
    }
  }


  renderDeleteIcon(ctx: CanvasRenderingContext2D, left: number, top: number, _styleOverride: any, fabricObject: fabric.Object): void {
    const size = 24;
    let deleteImg = new Image();
    deleteImg.src = this.deleteIcon;
    deleteImg.onload = () => {
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
      ctx.drawImage(deleteImg, -size / 2, -size / 2, size, size);
      ctx.restore();
    };
  }

  saveImage(): string {
    const dataURL = this.canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 1
    });
    return dataURL;
  }

  onChangeStrokeWidth(newSize: number) {
    const activeObjects = this.canvas.getActiveObjects();
    this.sizeValue = newSize;
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => {
        obj.set('strokeWidth', newSize);
      });
      this.canvas.requestRenderAll();
    }
  }

  onChangeFontSize(sizeFontValue: number) {
    const activeObjects = this.canvas.getActiveObjects();
    this.sizeFontValue = sizeFontValue;
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => {
        obj.set('fontSize', sizeFontValue);
      });
      this.canvas.requestRenderAll();
    }
  }

  onChangeColor(color: string): void {
    this.selectedColor = color;
    const activeObjects = this.canvas.getActiveObjects();
    if (activeObjects.length > 0) {
      activeObjects.forEach(obj => {
        if (obj instanceof fabric.Textbox || obj instanceof fabric.Text) {
          obj.set('fill', color);
        } else if (obj instanceof fabric.Path || obj instanceof fabric.Line || obj instanceof fabric.Rect || obj instanceof fabric.Circle) {
          obj.set('stroke', color);
        } else if (obj instanceof fabric.Group) {
          this.onChangeArrowColor(obj, color);
        }
      });
      this.canvas.requestRenderAll();
    }
  }

  onChangeArrowColor(arrowGroup: fabric.Group, color: string): void {
    arrowGroup.getObjects().forEach((obj) => {
      if (obj instanceof fabric.Line) {
        obj.set({stroke: color});
      } else if (obj instanceof fabric.Triangle) {
        obj.set({fill: color});
      }
    });
    this.canvas.renderAll();
  }

}


